import { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { Modal } from '@/components/ui/Modal'
import { signDocument } from '@/api/documents'
import type { AgreementSection } from './agreementTexts'

interface DocumentSigningModalProps {
  isOpen: boolean
  onClose: () => void
  onSigned: () => void
  documentType: string
  documentVersion: string
  title: string
  sections: AgreementSection[]
}

export function DocumentSigningModal({
  isOpen,
  onClose,
  onSigned,
  documentType,
  documentVersion,
  title,
  sections,
}: DocumentSigningModalProps) {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [hasSigned, setHasSigned] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleClear = () => {
    sigCanvasRef.current?.clear()
    setHasSigned(false)
  }

  const handleSignatureEnd = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      setHasSigned(true)
    }
  }

  const handleSubmit = async () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setError('Please draw your signature')
      return
    }
    if (!agreed) {
      setError('Please check the agreement box')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) throw new Error('Not authenticated')

      const signatureData = sigCanvasRef.current.toDataURL('image/png')

      await signDocument(token, {
        documentType,
        documentVersion,
        signatureData,
      })

      setSuccess(true)
      setTimeout(() => {
        onSigned()
        handleClose()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign document')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setAgreed(false)
    setHasSigned(false)
    setError(null)
    setSuccess(false)
    sigCanvasRef.current?.clear()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      maxWidth="max-w-2xl"
    >
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Document Signed</h3>
          <p className="text-sm text-gray-600">Your signature has been recorded.</p>
        </div>
      ) : (
        <>
          {/* Scrollable document content */}
          <div className="max-h-[40vh] overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50 text-sm text-gray-800 leading-relaxed">
            {sections.map((section, i) => (
              <div key={i} className={i > 0 ? 'mt-4' : ''}>
                <h3 className="font-bold text-gray-900 mb-2 text-sm uppercase tracking-wide">
                  {section.heading}
                </h3>
                <div className="whitespace-pre-wrap">{section.content}</div>
              </div>
            ))}
          </div>

          {/* Signature section */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Signature
            </label>
            <div className="border-2 border-gray-300 rounded-lg bg-white relative">
              <SignatureCanvas
                ref={sigCanvasRef}
                canvasProps={{
                  className: 'w-full',
                  style: { width: '100%', height: '150px' },
                }}
                onEnd={handleSignatureEnd}
              />
              {!hasSigned && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-400 text-sm">Draw your signature here</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="mt-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear Signature
            </button>
          </div>

          {/* Agreement checkbox */}
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#5c5b44] focus:ring-[#6b7850]"
            />
            <span className="text-sm text-gray-700">
              I have read and agree to this {title}
            </span>
          </label>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!hasSigned || !agreed || isSubmitting}
            className="w-full py-3 bg-[#6b7850] text-white rounded-lg font-medium hover:bg-[#5c5b44] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Sign & Submit'
            )}
          </button>
        </>
      )}
    </Modal>
  )
}
